import { Plus, SlidersHorizontal } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";

const BADGE_COLORS = {
  NEW: "bg-emerald-600",
  HOT: "bg-red-500",
  SALE: "bg-[var(--brand-accent)]",
  RESTOCKED: "bg-indigo-600",
};

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { currency, labelFor } = useSettings();
  const navigate = useNavigate();
  const badge = labelFor(product.subcategory || product.category);
  const hasVariants = (product.variants || []).length > 0;
  const onSale = product.sale_price != null && product.sale_price !== "" && Number(product.sale_price) < Number(product.price);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasVariants) {
      navigate(`/product/${product.id}`);
      return;
    }
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group bg-white border border-[#E8E4D9] rounded-xl overflow-hidden transition-all duration-300 hover:shadow-[0_8px_30px_rgba(43,91,58,0.10)] hover:-translate-y-1 flex flex-col"
    >
      <div className="aspect-square overflow-hidden bg-[#F4EFE6] relative">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 bg-[#F4EFE6] text-[#1A231C] text-xs font-medium px-2.5 py-0.5 rounded-full">
          {badge}
        </span>
        {product.badge && (
          <span
            data-testid={`product-badge-${product.id}`}
            className={`absolute top-3 right-3 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide ${BADGE_COLORS[product.badge] || "bg-[var(--brand-primary)]"}`}
          >
            {product.badge}
          </span>
        )}
        {!product.in_stock && (
          <span className="absolute bottom-3 right-3 bg-[#1A231C] text-white text-xs font-bold px-2 py-1 rounded">
            Out of stock
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-display font-medium text-[#1A231C] leading-snug">{product.name}</h3>
        {product.unit && <p className="text-xs text-[#5C665D] mt-0.5">{product.unit}</p>}
        <p className="text-sm text-[#5C665D] mt-1 line-clamp-2 flex-1">{product.description}</p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-baseline gap-2">
            {onSale ? (
              <>
                <span className="font-display font-bold text-lg text-[var(--brand-accent)]">{currency}{product.sale_price}</span>
                <span className="text-sm text-[#9CA3AF] line-through">{currency}{product.price}</span>
              </>
            ) : (
              <span className="font-display font-bold text-lg text-[#1A231C]">
                {hasVariants ? "From " : ""}{currency}{product.price}
              </span>
            )}
          </div>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            disabled={!product.in_stock}
            className="flex items-center gap-1 bg-[var(--brand-primary)] text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-[var(--brand-primary-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {hasVariants ? <><SlidersHorizontal className="w-4 h-4" strokeWidth={2} /> Options</> : <><Plus className="w-4 h-4" strokeWidth={2} /> Add</>}
          </button>
        </div>
      </div>
    </Link>
  );
}
